import { useId, useLayoutEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Company } from '@/types/company.types'
import { Button, Input, useModalFooterSlot } from '@/components/ui'
import { useUpdateCompany } from '../hooks/useCompanies'
import { updateCompanySchema, type UpdateCompanyFormValues } from '../schemas/company.schemas'

export interface EditCompanyFormProps {
  company: Company
  onSuccess: () => void
  onCancel: () => void
}

export function EditCompanyForm({
  company,
  onSuccess,
  onCancel,
}: EditCompanyFormProps): React.ReactElement {
  const formId = useId()
  const setModalFooter = useModalFooterSlot()
  const navigate = useNavigate()
  const updateCompany = useUpdateCompany()

  const handleManageMembers = (): void => {
    onSuccess()
    navigate(`/companies/${company._id}#members`)
  }
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<UpdateCompanyFormValues>({
    resolver: zodResolver(updateCompanySchema),
    defaultValues: {
      name: company.name,
      code: company.code ?? '',
      color: company.color ?? '',
      status: company.status,
    },
  })
  const color = watch('color')
  const pickerColor = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color ?? '')
    ? color
    : '#6366f1'

  const onSubmit = (values: UpdateCompanyFormValues): void => {
    updateCompany.mutate(
      {
        id: company._id,
        payload: {
          name: values.name,
          code: values.code?.trim() || undefined,
          color: values.color || undefined,
          status: values.status,
        },
      },
      {
        onSuccess: () => onSuccess(),
      }
    )
  }

  useLayoutEffect(() => {
    if (!setModalFooter) return
    setModalFooter(
      <>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          form={formId}
          type="submit"
          disabled={!isDirty} loading={updateCompany.isPending}
        >
          {updateCompany.isPending ? 'Saving…' : 'Save'}
        </Button>
      </>
    )
    return () => setModalFooter(null)
  }, [setModalFooter, onCancel, isDirty, updateCompany.isPending, formId])

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Name"
        {...register('name')}
        error={errors.name?.message}
        autoComplete="off"
      />
      <Input
        label="Code"
        {...register('code')}
        error={errors.code?.message}
        placeholder="LOS-001"
        autoComplete="off"
      />
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Brand colour
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={pickerColor}
            onChange={(event) =>
              setValue('color', event.target.value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            className="h-9 w-12 cursor-pointer rounded border border-gray-300 bg-white p-1 dark:border-gray-600 dark:bg-gray-800"
            aria-label="Pick a colour"
          />
          <Input
            {...register('color')}
            placeholder="#6366f1"
            className="font-mono"
            error={errors.color?.message}
            autoComplete="off"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Used for the wallboard card, shifts chips and accents. Leave blank to use the default palette.
        </p>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Status
        </label>
        <select
          {...register('status')}
          className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {errors.status && (
          <p className="mt-1 text-sm text-danger">{errors.status.message}</p>
        )}
      </div>
      <Button
        type="button"
        variant="secondary"
        onClick={handleManageMembers}
        className="w-full sm:w-auto"
      >
        Manage members
      </Button>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        To add or remove members, open the company page and use the{' '}
        <Link
          to={`/companies/${company._id}#members`}
          className="font-medium text-primary hover:underline"
          onClick={onSuccess}
        >
          Members tab
        </Link>
        .
      </p>
      {!setModalFooter && (
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!isDirty} loading={updateCompany.isPending}
          >
            {updateCompany.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </form>
  )
}

EditCompanyForm.displayName = 'EditCompanyForm'
